import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { AudienceFeature } from '@/constants/leesritme'

import { ArrowUpRight, Backpack, GraduationCap, HeartHandshake, School, Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'
import { SectionHeading } from '@/components/generals/section-heading'

import {
    AUDIENCE_PARENTS,
    AUDIENCE_PUPILS,
    AUDIENCE_SCHOOL,
    AUDIENCE_TEACHERS,
    AUDIENCE_YOUNGEST,
    THEME_WEEKS,
    external
} from '@/constants/leesritme'

const MAX_WEEKS = Math.max(...THEME_WEEKS.map(item => item.weken))
const TOTAL_WEEKS = THEME_WEEKS.reduce((sum, item) => sum + item.weken, 0)

interface FeatureListProps {
    items: Array<AudienceFeature>
    tone?: 'dark' | 'light'
}

const FeatureList: FC<FeatureListProps> = ({ items, tone = 'dark' }): ReactNode => {
    const light = tone === 'light'

    return (
        <ul className='mt-6 grid gap-4'>
            {items.map(item => {
                const Icon = item.icon

                return (
                    <li key={item.title} className='flex gap-3'>
                        <span className={cn('grid size-9 shrink-0 place-items-center rounded-lg', light ? 'bg-white/10 text-sun' : 'bg-leaf-soft text-leaf')}>
                            <Icon className='size-[1.125rem]' aria-hidden='true' />
                        </span>

                        <span>
                            <span className={cn('block font-bold', light ? 'text-paper' : 'text-ink')}>
                                {item.title}
                            </span>

                            <span className={cn('block text-[0.9375rem] leading-relaxed', light ? 'text-[#c3cad8]' : 'text-muted-foreground')}>
                                {item.text}
                            </span>
                        </span>
                    </li>
                )
            })}
        </ul>
    )
}

interface AudienceCardProps {
    title: string
    eyebrow: string
    icon: LucideIcon
    items: Array<AudienceFeature>
    className?: string
}

const AudienceCard: FC<AudienceCardProps> = ({ title, eyebrow, icon: Icon, items, className }): ReactNode => {
    return (
        <article className={cn('reveal rounded-[1.75rem] border border-ink/10 bg-card p-7 shadow-soft', className)}>
            <div className='flex items-center gap-3'>
                <span className='grid size-12 place-items-center rounded-2xl bg-sun-soft text-ink'>
                    <Icon className='size-6' aria-hidden='true' />
                </span>

                <div>
                    <p className='text-xs font-bold tracking-[0.14em] text-muted-foreground uppercase'>
                        {eyebrow}
                    </p>

                    <h3 className='font-heading text-2xl font-semibold text-ink'>
                        {title}
                    </h3>
                </div>
            </div>

            <FeatureList items={items} />
        </article>
    )
}

export const OfferSection: FC = (): ReactNode => {
    return (
        <section id='aanbod' className='py-20 sm:py-28'>
            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <div className='grid gap-10 lg:grid-cols-[1fr_1fr] lg:items-end lg:gap-16'>
                    <SectionHeading
                        eyebrow='Wat je krijgt'
                        title='Dit krijgt je school.'
                        intro='Per groep 30 tot 47 themaweken, met elke week vijf teksten voor in de klas en vijf voor thuis. Er komen er elke week bij, tot er voor elke groep een volledig schooljaar klaarstaat.'
                    />

                    <figure className='reveal rounded-[1.75rem] border border-ink/10 bg-card p-6 shadow-soft sm:p-7'>
                        <figcaption className='flex flex-wrap items-baseline justify-between gap-2'>
                            <span className='font-heading text-xl font-semibold text-ink'>
                                Themaweken per groep
                            </span>

                            <span className='text-sm text-muted-foreground'>
                                {TOTAL_WEEKS} weken · 2380 teksten
                            </span>
                        </figcaption>

                        <ul className='mt-5 grid gap-2.5'>
                            {THEME_WEEKS.map(item => (
                                <li key={item.groep} className='grid grid-cols-[4.5rem_1fr_2rem] items-center gap-3 text-sm'>
                                    <span className='font-semibold text-muted-foreground'>
                                        Groep {item.groep}
                                    </span>

                                    <span className='h-3 overflow-hidden rounded-full bg-sand' aria-hidden='true'>
                                        <span className='block h-full rounded-full bg-leaf' style={{ width: `${(item.weken / MAX_WEEKS) * 100}%` }} />
                                    </span>

                                    <span className='text-right font-bold text-ink tabular-nums'>
                                        {item.weken}
                                    </span>
                                </li>
                            ))}
                        </ul>

                        <a
                            href={external('/themaweken')}
                            className='mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-leaf underline decoration-leaf/30 underline-offset-4 hover:decoration-leaf'
                        >
                            Bekijk alle themaweken
                            <ArrowUpRight className='size-4' aria-hidden='true' />
                        </a>
                    </figure>
                </div>

                <div className='mt-12 grid gap-6 lg:grid-cols-6'>
                    <article className='reveal relative flex flex-col overflow-hidden rounded-[1.75rem] bg-leaf p-7 text-paper lg:col-span-3 lg:row-span-2'>
                        <div className='flex items-center gap-3'>
                            <span className='grid size-12 place-items-center rounded-2xl bg-white/15 text-sun'>
                                <Backpack className='size-6' aria-hidden='true' />
                            </span>

                            <div>
                                <p className='text-xs font-bold tracking-[0.14em] text-white/70 uppercase'>
                                    Voor de leerling
                                </p>

                                <h3 className='font-heading text-2xl font-semibold'>
                                    Leerlingen
                                </h3>
                            </div>
                        </div>

                        <FeatureList items={AUDIENCE_PUPILS} tone='light' />

                        <div className='relative mt-auto -mb-7 flex justify-center gap-4 pt-10' aria-hidden='true'>
                            <div className='w-[46%] max-w-[13rem] translate-y-6 rounded-t-[2rem] bg-ink p-2 pb-0 shadow-phone sm:max-w-[14rem]'>
                                <div className='relative aspect-[9/12] overflow-hidden rounded-t-[1.5rem] bg-[#fbf7ef]'>
                                    <img src='/images/app/app-leesmanieren.webp' alt='' loading='lazy' decoding='async' className='absolute inset-x-0 top-0 h-auto w-full' />
                                </div>
                            </div>

                            <div className='w-[46%] max-w-[13rem] rounded-t-[2rem] bg-ink p-2 pb-0 shadow-phone sm:max-w-[14rem]'>
                                <div className='relative aspect-[9/12] overflow-hidden rounded-t-[1.5rem] bg-[#fbf7ef]'>
                                    <img src='/images/app/app-boek.webp' alt='' loading='lazy' decoding='async' className='absolute inset-x-0 top-0 h-auto w-full' />
                                </div>
                            </div>
                        </div>
                    </article>

                    <AudienceCard
                        eyebrow='Voor de leerkracht'
                        title='Leerkrachten'
                        icon={GraduationCap}
                        items={AUDIENCE_TEACHERS}
                        className='lg:col-span-3'
                    />

                    <AudienceCard
                        eyebrow='Voor thuis'
                        title='Ouders'
                        icon={HeartHandshake}
                        items={AUDIENCE_PARENTS}
                        className='lg:col-span-3'
                    />

                    <AudienceCard
                        eyebrow='Voor directie en ib'
                        title='School'
                        icon={School}
                        items={AUDIENCE_SCHOOL}
                        className='lg:col-span-3'
                    />

                    <article className='reveal rounded-[1.75rem] border border-coral/20 bg-coral-soft/60 p-7 lg:col-span-3'>
                        <div className='flex flex-wrap items-center justify-between gap-3'>
                            <div className='flex items-center gap-3'>
                                <span className='grid size-12 place-items-center rounded-2xl bg-white text-coral-deep'>
                                    <Sparkles className='size-6' aria-hidden='true' />
                                </span>

                                <div>
                                    <p className='text-xs font-bold tracking-[0.14em] text-muted-foreground uppercase'>
                                        De jongste kinderen
                                    </p>

                                    <h3 className='font-heading text-2xl font-semibold text-ink'>
                                        Groep 1 en 2
                                    </h3>
                                </div>
                            </div>

                            <span className='rounded-full bg-white px-3 py-1 text-xs font-bold text-coral-deep'>
                                Zonder inlog, zonder meerprijs
                            </span>
                        </div>

                        <ul className='mt-6 grid gap-4 sm:grid-cols-2'>
                            {AUDIENCE_YOUNGEST.map(item => {
                                const Icon = item.icon

                                return (
                                    <li key={item.title} className='flex flex-col rounded-2xl bg-white p-5'>
                                        <Icon className='size-6 text-coral-deep' aria-hidden='true' />

                                        <span className='mt-3 font-bold text-ink'>
                                            {item.title}
                                        </span>

                                        <span className='mt-1 text-[0.9375rem] leading-relaxed text-muted-foreground'>
                                            {item.text}
                                        </span>

                                        <a
                                            href={item.link.href}
                                            className='mt-auto inline-flex items-center gap-1.5 pt-4 text-sm font-bold text-coral-deep underline decoration-coral-deep/30 underline-offset-4 hover:decoration-coral-deep'
                                        >
                                            {item.link.label}
                                            <ArrowUpRight className='size-4' aria-hidden='true' />
                                        </a>
                                    </li>
                                )
                            })}
                        </ul>
                    </article>
                </div>
            </div>
        </section>
    )
}
